import { useEffect, useRef, useState, type CSSProperties, type FormEvent } from "react";
import { Pencil, Repeat, Trash2 } from "lucide-react";
import { canManageExpenses } from "../lib/access";
import { ApiError } from "../lib/api/client";
import {
  cancelExpense,
  cancelExpenseSeries,
  createExpense,
  deleteExpense,
  listExpenses,
  payExpense,
  updateExpense,
  updateExpenseSeries,
} from "../lib/api/endpoints";
import {
  EXPENSE_CATEGORY,
  EXPENSE_CATEGORY_LABELS,
  EXPENSE_RECURRENCE_TYPE,
  EXPENSE_RECURRENCE_TYPE_LABELS,
  EXPENSE_STATUS,
  PAYMENT_METHOD_LABELS,
  expenseCategoryLabel,
  expenseRecurrenceTypeLabel,
  paymentMethodLabel,
} from "../lib/enums";
import { money } from "../lib/format";
import { useSession } from "../lib/session/SessionContext";
import { C, font } from "../theme";
import type { CreateExpenseRequest, ExpenseDto, PayExpenseRequest, UpdateExpenseRequest, UpdateExpenseSeriesRequest } from "../types";
import { Badge } from "./Badge";
import { Button } from "./Button";
import { Card } from "./Card";
import { ConfirmDialog } from "./ConfirmDialog";
import { DateTimePicker } from "./DateTimePicker";
import { FieldInput, FieldTextarea } from "./Field";
import { Label } from "./Label";
import { Select } from "./Select";

const EXPENSE_PAGE_SIZE = 10;

const CATEGORY_FILTER_OPTIONS = [
  { value: "", label: "Todas as categorias" },
  ...Object.entries(EXPENSE_CATEGORY_LABELS).map(([value, label]) => ({ value, label })),
];

const STATUS_FILTER_OPTIONS = [
  { value: "", label: "Todos os status" },
  { value: String(EXPENSE_STATUS.Pending), label: "Pendente" },
  { value: String(EXPENSE_STATUS.Paid), label: "Paga" },
  { value: String(EXPENSE_STATUS.Cancelled), label: "Cancelada" },
];

const RECURRENCE_OPTIONS = [
  { value: String(EXPENSE_RECURRENCE_TYPE.None), label: "Sem recorrência" },
  { value: String(EXPENSE_RECURRENCE_TYPE.Weekly), label: "Semanal" },
  { value: String(EXPENSE_RECURRENCE_TYPE.Monthly), label: "Mensal" },
  { value: String(EXPENSE_RECURRENCE_TYPE.Yearly), label: "Anual" },
];

const PAYMENT_OPTIONS = Object.entries(PAYMENT_METHOD_LABELS).map(([value, label]) => ({ value, label }));

const statusBadge: Record<number, { text: string; bg: string; color: string }> = {
  [EXPENSE_STATUS.Pending]: { text: "Pendente", bg: C.limeSoft, color: C.ink },
  [EXPENSE_STATUS.Paid]: { text: "Paga", bg: C.faint, color: C.gray },
  [EXPENSE_STATUS.Cancelled]: { text: "Cancelada", bg: C.faint, color: C.light },
};

function errorMessage(error: unknown, fallback: string): string {
  return error instanceof ApiError ? error.detail ?? error.message : fallback;
}

function labelStyle(): CSSProperties {
  return { display: "block", color: C.gray, fontSize: 12, fontWeight: 600, margin: "16px 0 4px" };
}

function errorTextStyle(): CSSProperties {
  return { color: C.red, fontSize: 11.5, marginTop: 8 };
}

function formatDate(value: string): string {
  return new Date(`${value}T00:00:00`).toLocaleDateString("pt-BR");
}

type ExpenseFormPayload = CreateExpenseRequest;

function ExpenseForm({
  initialExpense,
  submitting,
  submitError,
  onSubmit,
  onCancel,
}: {
  initialExpense?: ExpenseDto | null;
  submitting: boolean;
  submitError: string | null;
  onSubmit: (payload: ExpenseFormPayload) => void;
  onCancel: () => void;
}) {
  const isEditing = Boolean(initialExpense);
  const [description, setDescription] = useState(initialExpense?.description ?? "");
  const [amount, setAmount] = useState(initialExpense ? String(initialExpense.amount) : "");
  const [category, setCategory] = useState(String(initialExpense?.category ?? EXPENSE_CATEGORY.Other));
  const [vendor, setVendor] = useState(initialExpense?.vendor ?? "");
  const [notes, setNotes] = useState(initialExpense?.notes ?? "");
  const [dueDate, setDueDate] = useState(initialExpense?.dueDate ?? "");
  const [recurrenceType, setRecurrenceType] = useState(
    String(initialExpense?.recurrenceType ?? EXPENSE_RECURRENCE_TYPE.None),
  );
  const [recurrenceInterval, setRecurrenceInterval] = useState(
    initialExpense && initialExpense.recurrenceType !== EXPENSE_RECURRENCE_TYPE.None
      ? String(initialExpense.recurrenceInterval)
      : "1",
  );
  const [recurrenceEndDate, setRecurrenceEndDate] = useState(initialExpense?.recurrenceEndDate ?? "");
  const [errors, setErrors] = useState<{
    description?: string;
    amount?: string;
    category?: string;
    vendor?: string;
    notes?: string;
    dueDate?: string;
    recurrenceInterval?: string;
    recurrenceEndDate?: string;
  }>({});

  const hasRecurrence = Number(recurrenceType) !== EXPENSE_RECURRENCE_TYPE.None;

  const submit = (event: FormEvent) => {
    event.preventDefault();
    if (submitting) return;
    const trimmedDescription = description.trim();
    const trimmedVendor = vendor.trim();
    const trimmedNotes = notes.trim();
    const parsedAmount = Number(amount);
    const parsedInterval = Number(recurrenceInterval);
    const nextErrors: typeof errors = {};
    if (!trimmedDescription) nextErrors.description = "Informe a descrição da despesa";
    if (trimmedDescription.length > 200) nextErrors.description = "Descrição muito longa (máx. 200 caracteres)";
    if (amount === "" || !Number.isFinite(parsedAmount) || parsedAmount <= 0) nextErrors.amount = "Informe um valor válido";
    if (!dueDate) nextErrors.dueDate = "Informe a data de vencimento";
    if (trimmedVendor.length > 200) nextErrors.vendor = "Fornecedor muito longo (máx. 200 caracteres)";
    if (trimmedNotes.length > 1000) nextErrors.notes = "Observações muito longas (máx. 1000 caracteres)";
    if (hasRecurrence && (!recurrenceInterval || !Number.isInteger(parsedInterval) || parsedInterval < 1)) {
      nextErrors.recurrenceInterval = "O intervalo deve ser maior ou igual a 1";
    }
    if (hasRecurrence && recurrenceEndDate && recurrenceEndDate <= dueDate) {
      nextErrors.recurrenceEndDate = "O fim da recorrência deve ser após o vencimento";
    }
    setErrors(nextErrors);
    if (Object.keys(nextErrors).length > 0) return;

    const base = {
      description: trimmedDescription,
      amount: parsedAmount,
      category: Number(category),
      vendor: trimmedVendor || null,
      notes: trimmedNotes || null,
      dueDate,
    };
    if (isEditing) {
      onSubmit(base);
      return;
    }
    const payload: CreateExpenseRequest = { ...base };
    if (hasRecurrence) {
      payload.recurrenceType = Number(recurrenceType);
      payload.recurrenceInterval = parsedInterval;
      payload.recurrenceEndDate = recurrenceEndDate || null;
    } else {
      payload.recurrenceType = null;
      payload.recurrenceInterval = null;
      payload.recurrenceEndDate = null;
    }
    onSubmit(payload);
  };

  return (
    <div role="dialog" aria-modal="true" aria-labelledby="expense-form-title" style={{ position: "fixed", inset: 0, background: C.overlay, display: "flex", alignItems: "center", justifyContent: "center", zIndex: 50, padding: 16 }}>
      <Card style={{ width: "100%", maxWidth: 560, maxHeight: "92vh", overflowY: "auto" }}>
        <div style={{ display: "flex", justifyContent: "space-between", gap: 12, alignItems: "center" }}>
          <div id="expense-form-title"><Label>{isEditing ? "Editar despesa" : "Nova despesa"}</Label></div>
          <Button variant="ghost" onClick={onCancel} disabled={submitting} ariaLabel="Fechar">X</Button>
        </div>
        <form onSubmit={submit} noValidate>
          <label htmlFor="expense-description" style={labelStyle()}>Descrição</label>
          <FieldInput id="expense-description" value={description} maxLength={200} disabled={submitting} onChange={event => setDescription(event.target.value)} />
          {errors.description && <div role="alert" style={errorTextStyle()}>{errors.description}</div>}

          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(160px,1fr))", gap: 12 }}>
            <div>
              <label htmlFor="expense-amount" style={labelStyle()}>Valor (R$)</label>
              <FieldInput id="expense-amount" type="number" min="0.01" step="0.01" value={amount} disabled={submitting} onChange={event => setAmount(event.target.value)} />
              {errors.amount && <div role="alert" style={errorTextStyle()}>{errors.amount}</div>}
            </div>
            <div>
              <label htmlFor="expense-category" style={labelStyle()}>Categoria</label>
              <Select id="expense-category" ariaLabel="Categoria" value={category} options={CATEGORY_FILTER_OPTIONS.slice(1)} disabled={submitting} onChange={setCategory} style={{ width: "100%" }} />
              {errors.category && <div role="alert" style={errorTextStyle()}>{errors.category}</div>}
            </div>
          </div>

          <label htmlFor="expense-vendor" style={labelStyle()}>Fornecedor</label>
          <FieldInput id="expense-vendor" value={vendor} maxLength={200} disabled={submitting} onChange={event => setVendor(event.target.value)} />
          {errors.vendor && <div role="alert" style={errorTextStyle()}>{errors.vendor}</div>}

          <label htmlFor="expense-due-date" style={labelStyle()}>Vencimento</label>
          <DateTimePicker id="expense-due-date" ariaLabel="Vencimento" value={dueDate} mode="date" placement="below" disabled={submitting} onChange={setDueDate} />
          {errors.dueDate && <div role="alert" style={errorTextStyle()}>{errors.dueDate}</div>}

          <label htmlFor="expense-notes" style={labelStyle()}>Observações</label>
          <FieldTextarea id="expense-notes" value={notes} maxLength={1000} disabled={submitting} onChange={event => setNotes(event.target.value)} />
          {errors.notes && <div role="alert" style={errorTextStyle()}>{errors.notes}</div>}

          {!isEditing && (
            <>
              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(140px,1fr))", gap: 12 }}>
                <div>
                  <label htmlFor="expense-recurrence" style={labelStyle()}>Recorrência</label>
                  <Select id="expense-recurrence" ariaLabel="Recorrência" value={recurrenceType} options={RECURRENCE_OPTIONS} disabled={submitting} onChange={setRecurrenceType} style={{ width: "100%" }} />
                </div>
                {hasRecurrence && (
                  <div>
                    <label htmlFor="expense-recurrence-interval" style={labelStyle()}>Intervalo</label>
                    <FieldInput id="expense-recurrence-interval" type="number" min="1" step="1" value={recurrenceInterval} disabled={submitting} onChange={event => setRecurrenceInterval(event.target.value)} />
                    {errors.recurrenceInterval && <div role="alert" style={errorTextStyle()}>{errors.recurrenceInterval}</div>}
                  </div>
                )}
              </div>
              {hasRecurrence && (
                <>
                  <label htmlFor="expense-recurrence-end" style={labelStyle()}>Fim da recorrência</label>
                  <DateTimePicker id="expense-recurrence-end" ariaLabel="Fim da recorrência" value={recurrenceEndDate} mode="date" placement="below" disabled={submitting} onChange={setRecurrenceEndDate} />
                  {errors.recurrenceEndDate && <div role="alert" style={errorTextStyle()}>{errors.recurrenceEndDate}</div>}
                </>
              )}
            </>
          )}

          {submitError && <div role="alert" style={errorTextStyle()}>{submitError}</div>}
          <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, marginTop: 18 }}>
            <Button variant="outline" onClick={onCancel} disabled={submitting}>Cancelar</Button>
            <Button type="submit" disabled={submitting}>{submitting ? "Salvando..." : isEditing ? "Salvar despesa" : "Criar despesa"}</Button>
          </div>
        </form>
      </Card>
    </div>
  );
}

function ExpenseSeriesForm({
  expense,
  submitting,
  submitError,
  onSubmit,
  onCancel,
}: {
  expense: ExpenseDto;
  submitting: boolean;
  submitError: string | null;
  onSubmit: (payload: UpdateExpenseSeriesRequest) => void;
  onCancel: () => void;
}) {
  const [description, setDescription] = useState(expense.description);
  const [amount, setAmount] = useState(String(expense.amount));
  const [category, setCategory] = useState(String(expense.category));
  const [vendor, setVendor] = useState(expense.vendor ?? "");
  const [notes, setNotes] = useState(expense.notes ?? "");
  const [errors, setErrors] = useState<{ description?: string; amount?: string; vendor?: string; notes?: string }>({});

  const submit = (event: FormEvent) => {
    event.preventDefault();
    if (submitting) return;
    const trimmedDescription = description.trim();
    const trimmedVendor = vendor.trim();
    const trimmedNotes = notes.trim();
    const parsedAmount = Number(amount);
    const nextErrors: typeof errors = {};
    if (!trimmedDescription) nextErrors.description = "Informe a descrição da despesa";
    if (trimmedDescription.length > 200) nextErrors.description = "Descrição muito longa (máx. 200 caracteres)";
    if (amount === "" || !Number.isFinite(parsedAmount) || parsedAmount <= 0) nextErrors.amount = "Informe um valor válido";
    if (trimmedVendor.length > 200) nextErrors.vendor = "Fornecedor muito longo (máx. 200 caracteres)";
    if (trimmedNotes.length > 1000) nextErrors.notes = "Observações muito longas (máx. 1000 caracteres)";
    setErrors(nextErrors);
    if (Object.keys(nextErrors).length > 0) return;
    onSubmit({ description: trimmedDescription, amount: parsedAmount, category: Number(category), vendor: trimmedVendor || null, notes: trimmedNotes || null });
  };

  return (
    <div role="dialog" aria-modal="true" aria-labelledby="expense-series-title" style={{ position: "fixed", inset: 0, background: C.overlay, display: "flex", alignItems: "center", justifyContent: "center", zIndex: 50, padding: 16 }}>
      <Card style={{ width: "100%", maxWidth: 560 }}>
        <div style={{ display: "flex", justifyContent: "space-between", gap: 12, alignItems: "center" }}>
          <div id="expense-series-title"><Label>Editar série recorrente</Label></div>
          <Button variant="ghost" onClick={onCancel} disabled={submitting} ariaLabel="Fechar">X</Button>
        </div>
        <form onSubmit={submit} noValidate>
          <label htmlFor="series-description" style={labelStyle()}>Descrição</label>
          <FieldInput id="series-description" value={description} maxLength={200} disabled={submitting} onChange={event => setDescription(event.target.value)} />
          {errors.description && <div role="alert" style={errorTextStyle()}>{errors.description}</div>}

          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(160px,1fr))", gap: 12 }}>
            <div>
              <label htmlFor="series-amount" style={labelStyle()}>Valor (R$)</label>
              <FieldInput id="series-amount" type="number" min="0.01" step="0.01" value={amount} disabled={submitting} onChange={event => setAmount(event.target.value)} />
              {errors.amount && <div role="alert" style={errorTextStyle()}>{errors.amount}</div>}
            </div>
            <div>
              <label htmlFor="series-category" style={labelStyle()}>Categoria</label>
              <Select id="series-category" ariaLabel="Categoria" value={category} options={CATEGORY_FILTER_OPTIONS.slice(1)} disabled={submitting} onChange={setCategory} style={{ width: "100%" }} />
            </div>
          </div>

          <label htmlFor="series-vendor" style={labelStyle()}>Fornecedor</label>
          <FieldInput id="series-vendor" value={vendor} maxLength={200} disabled={submitting} onChange={event => setVendor(event.target.value)} />
          {errors.vendor && <div role="alert" style={errorTextStyle()}>{errors.vendor}</div>}

          <label htmlFor="series-notes" style={labelStyle()}>Observações</label>
          <FieldTextarea id="series-notes" value={notes} maxLength={1000} disabled={submitting} onChange={event => setNotes(event.target.value)} />
          {errors.notes && <div role="alert" style={errorTextStyle()}>{errors.notes}</div>}

          {submitError && <div role="alert" style={errorTextStyle()}>{submitError}</div>}
          <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, marginTop: 18 }}>
            <Button variant="outline" onClick={onCancel} disabled={submitting}>Cancelar</Button>
            <Button type="submit" disabled={submitting}>{submitting ? "Salvando..." : "Salvar série"}</Button>
          </div>
        </form>
      </Card>
    </div>
  );
}

export function ExpensesTab() {
  const { accessToken, activeTenant } = useSession();
  const token = accessToken ?? "";
  const canManage = canManageExpenses(activeTenant?.roleSlug ?? null);
  const mutationRef = useRef(false);
  const [expenses, setExpenses] = useState<ExpenseDto[] | null>(null);
  const [totalCount, setTotalCount] = useState(0);
  const [statusFilter, setStatusFilter] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("");
  const [dueDateFrom, setDueDateFrom] = useState("");
  const [dueDateTo, setDueDateTo] = useState("");
  const [seriesGroupId, setSeriesGroupId] = useState<string | null>(null);
  const [seriesDescription, setSeriesDescription] = useState("");
  const [page, setPage] = useState(1);
  const [reloadKey, setReloadKey] = useState(0);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [formExpense, setFormExpense] = useState<ExpenseDto | null | undefined>(undefined);
  const [seriesEditExpense, setSeriesEditExpense] = useState<ExpenseDto | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [payingExpense, setPayingExpense] = useState<ExpenseDto | null>(null);
  const [paymentMethod, setPaymentMethod] = useState("1");
  const [pendingCancel, setPendingCancel] = useState<ExpenseDto | null>(null);
  const [pendingDelete, setPendingDelete] = useState<ExpenseDto | null>(null);
  const [pendingSeriesCancel, setPendingSeriesCancel] = useState<ExpenseDto | null>(null);
  const [processing, setProcessing] = useState(false);

  useEffect(() => {
    if (!token) return;
    let cancelled = false;
    setExpenses(null);
    setLoadError(null);
    const status = statusFilter === "" ? undefined : Number(statusFilter);
    const category = categoryFilter === "" ? undefined : Number(categoryFilter);
    listExpenses(token, {
      page,
      pageSize: EXPENSE_PAGE_SIZE,
      status,
      category,
      dueDateFrom: dueDateFrom || undefined,
      dueDateTo: dueDateTo || undefined,
      recurrenceGroupId: seriesGroupId ?? undefined,
    }).then(result => {
      if (!cancelled) {
        setExpenses(result.items);
        setTotalCount(result.totalCount);
      }
    }).catch(error => {
      if (!cancelled) {
        setExpenses([]);
        setLoadError(errorMessage(error, "Falha ao carregar despesas."));
      }
    });
    return () => { cancelled = true; };
  }, [token, page, statusFilter, categoryFilter, dueDateFrom, dueDateTo, seriesGroupId, reloadKey]);

  const reload = () => setReloadKey(value => value + 1);

  const submitExpense = async (payload: CreateExpenseRequest | UpdateExpenseRequest) => {
    if (!token || mutationRef.current) return;
    mutationRef.current = true;
    setSubmitting(true);
    setActionError(null);
    try {
      if (formExpense) {
        await updateExpense(token, formExpense.id, payload as UpdateExpenseRequest);
        setSuccess("Despesa atualizada com sucesso.");
      } else {
        await createExpense(token, payload as CreateExpenseRequest);
        setSuccess("Despesa criada com sucesso.");
      }
      setFormExpense(undefined);
      reload();
    } catch (error) {
      setActionError(errorMessage(error, "Falha ao salvar despesa."));
    } finally {
      mutationRef.current = false;
      setSubmitting(false);
    }
  };

  const submitSeries = async (payload: UpdateExpenseSeriesRequest) => {
    if (!token || !seriesEditExpense?.recurrenceGroupId || mutationRef.current) return;
    mutationRef.current = true;
    setSubmitting(true);
    setActionError(null);
    try {
      await updateExpenseSeries(token, seriesEditExpense.recurrenceGroupId, payload);
      setSeriesEditExpense(null);
      setSuccess("Série atualizada com sucesso.");
      reload();
    } catch (error) {
      setActionError(errorMessage(error, "Falha ao atualizar a série."));
    } finally {
      mutationRef.current = false;
      setSubmitting(false);
    }
  };

  const confirmPay = async () => {
    if (!token || !payingExpense || mutationRef.current) return;
    mutationRef.current = true;
    setProcessing(true);
    setActionError(null);
    try {
      await payExpense(token, payingExpense.id, { paymentMethod: Number(paymentMethod) } satisfies PayExpenseRequest);
      setPayingExpense(null);
      setSuccess("Despesa paga com sucesso.");
      reload();
    } catch (error) {
      setActionError(errorMessage(error, "Falha ao pagar a despesa."));
    } finally {
      mutationRef.current = false;
      setProcessing(false);
    }
  };

  const confirmCancel = async () => {
    if (!token || !pendingCancel || mutationRef.current) return;
    mutationRef.current = true;
    setProcessing(true);
    setActionError(null);
    try {
      await cancelExpense(token, pendingCancel.id);
      setPendingCancel(null);
      setSuccess("Despesa cancelada com sucesso.");
      reload();
    } catch (error) {
      setActionError(errorMessage(error, "Falha ao cancelar a despesa."));
    } finally {
      mutationRef.current = false;
      setProcessing(false);
    }
  };

  const confirmDelete = async () => {
    if (!token || !pendingDelete || mutationRef.current) return;
    mutationRef.current = true;
    setProcessing(true);
    setActionError(null);
    try {
      await deleteExpense(token, pendingDelete.id);
      setPendingDelete(null);
      setSuccess("Despesa excluída com sucesso.");
      reload();
    } catch (error) {
      setActionError(errorMessage(error, "Falha ao excluir a despesa."));
    } finally {
      mutationRef.current = false;
      setProcessing(false);
    }
  };

  const confirmSeriesCancel = async () => {
    if (!token || !pendingSeriesCancel?.recurrenceGroupId || mutationRef.current) return;
    mutationRef.current = true;
    setProcessing(true);
    setActionError(null);
    try {
      await cancelExpenseSeries(token, pendingSeriesCancel.recurrenceGroupId);
      setPendingSeriesCancel(null);
      setSeriesGroupId(null);
      setSuccess("Série cancelada com sucesso.");
      reload();
    } catch (error) {
      setActionError(errorMessage(error, "Falha ao cancelar a série."));
    } finally {
      mutationRef.current = false;
      setProcessing(false);
    }
  };

  const viewSeries = (expense: ExpenseDto) => {
    if (!expense.recurrenceGroupId) return;
    setSeriesGroupId(expense.recurrenceGroupId);
    setSeriesDescription(expense.description);
    setPage(1);
  };

  const totalPages = Math.max(1, Math.ceil(totalCount / EXPENSE_PAGE_SIZE));
  const dialogOpen = formExpense !== undefined || seriesEditExpense !== null || payingExpense !== null || pendingCancel !== null || pendingDelete !== null || pendingSeriesCancel !== null;

  return <div style={{ display: "grid", gap: 14 }}>
    {success && <div role="status" style={{ color: C.ink, background: C.limeSoft, borderRadius: 10, padding: 12, fontSize: 12.5 }}>{success}</div>}
    {loadError && <div role="alert" style={{ color: C.red, background: C.white, border: `1px solid ${C.faint}`, borderRadius: 10, padding: 12, fontSize: 12.5 }}>{loadError} <Button variant="ghost" onClick={reload}>Tentar novamente</Button></div>}
    {actionError && !dialogOpen && <div role="alert" style={{ color: C.red, fontSize: 12 }}>{actionError}</div>}
    <Card>
      <div style={{ display: "flex", justifyContent: "space-between", gap: 12, alignItems: "flex-start", flexWrap: "wrap" }}>
        <div><Label>Despesas</Label><div style={{ color: C.light, fontSize: 12, marginTop: 3 }}>Registre e acompanhe as contas a pagar do CT.</div></div>
        {canManage ? <Button onClick={() => { setActionError(null); setFormExpense(null); }}>+ Nova despesa</Button> : <span style={{ color: C.light, background: C.bg, borderRadius: 12, padding: "5px 9px", fontSize: 11.5 }}>Somente leitura</span>}
      </div>
      <div style={{ display: "flex", gap: 10, flexWrap: "wrap", marginTop: 14, alignItems: "center" }}>
        <Select ariaLabel="Filtrar despesas por status" value={statusFilter} options={STATUS_FILTER_OPTIONS} onChange={value => { setStatusFilter(value); setPage(1); }} style={{ width: 180 }} />
        <Select ariaLabel="Filtrar despesas por categoria" value={categoryFilter} options={CATEGORY_FILTER_OPTIONS} onChange={value => { setCategoryFilter(value); setPage(1); }} style={{ width: 220 }} />
        <div style={{ display: "flex", gap: 6, alignItems: "center", color: C.gray, fontSize: 12 }}>
          Vencimento
          <span style={{ width: 150 }}><DateTimePicker ariaLabel="Vencimento de" value={dueDateFrom} mode="date" placement="below" onChange={value => { setDueDateFrom(value); setPage(1); }} /></span>
          <span>a</span>
          <span style={{ width: 150 }}><DateTimePicker ariaLabel="Vencimento até" value={dueDateTo} mode="date" placement="below" onChange={value => { setDueDateTo(value); setPage(1); }} /></span>
        </div>
        <Button variant="outline" style={{ fontSize: 11.5 }} onClick={() => { setStatusFilter(""); setCategoryFilter(""); setDueDateFrom(""); setDueDateTo(""); setPage(1); }}>Limpar filtros</Button>
      </div>
      {seriesGroupId && (
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12, marginTop: 12, background: C.limeSoft, borderRadius: 10, padding: "10px 12px" }}>
          <span style={{ fontSize: 12.5, color: C.ink }}><b>Série:</b> {seriesDescription}</span>
          <Button variant="ghost" style={{ fontSize: 11.5 }} onClick={() => { setSeriesGroupId(null); setPage(1); }}>Ver todas as despesas</Button>
        </div>
      )}
      {expenses === null && !loadError && <div style={{ color: C.light, padding: "22px 0", fontSize: 13 }}>Carregando despesas...</div>}
      {expenses?.length === 0 && !loadError && <div style={{ color: C.light, padding: "22px 0", fontSize: 13 }}>Nenhuma despesa encontrada.</div>}
      {expenses && expenses.length > 0 && <div style={{ display: "grid", gap: 8, marginTop: 14 }}>{expenses.map(expense => {
        const badge = statusBadge[expense.status] ?? { text: `Status ${expense.status}`, bg: C.faint, color: C.gray };
        const isRecurring = Boolean(expense.recurrenceGroupId) || expense.recurrenceType !== EXPENSE_RECURRENCE_TYPE.None;
        const isPending = expense.status === EXPENSE_STATUS.Pending;
        return (
          <div key={expense.id} style={{ border: `1px solid ${C.faint}`, borderRadius: 10, padding: 12, display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12 }}>
            <div style={{ display: "grid", gap: 4 }}>
              <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
                <b style={{ color: C.ink, fontSize: 13.5 }}>{expense.description}</b>
                <Badge bg={badge.bg} color={badge.color}>{badge.text}</Badge>
                {isPending && expense.isOverdue && <Badge bg={C.red} color={C.white}>Em atraso</Badge>}
                <span style={{ color: C.gray, background: C.bg, borderRadius: 10, padding: "2px 7px", fontSize: 10.5, fontWeight: 700 }}>{expenseCategoryLabel(expense.category)}</span>
                {isRecurring && <span style={{ display: "inline-flex", gap: 4, alignItems: "center", color: C.gray, background: C.bg, borderRadius: 10, padding: "2px 7px", fontSize: 10.5, fontWeight: 700 }}><Repeat size={11} />{expenseRecurrenceTypeLabel(expense.recurrenceType)}</span>}
              </div>
              <div style={{ display: "flex", gap: 6, flexWrap: "wrap", alignItems: "center", color: C.light, fontSize: 11.5 }}>
                <span style={{ fontWeight: 700, color: C.ink, fontSize: 13 }}>{money(expense.amount)}</span>
                <span>• vence {formatDate(expense.dueDate)}</span>
                {expense.vendor && <span>• {expense.vendor}</span>}
                {expense.paidAt && <span>• pago em {new Date(expense.paidAt).toLocaleString("pt-BR")} ({paymentMethodLabel(expense.paymentMethod ?? 0)})</span>}
              </div>
              {expense.notes && <span style={{ color: C.light, fontSize: 11.5 }}>{expense.notes}</span>}
            </div>
            {canManage && (
              <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
                {isPending && <Button style={{ fontSize: 11.5, padding: "6px 12px" }} onClick={() => { setActionError(null); setPaymentMethod("1"); setPayingExpense(expense); }}>Pagar</Button>}
                {isPending && <Button variant="ghost" ariaLabel={`Editar ${expense.description}`} title="Editar" onClick={() => { setActionError(null); setFormExpense(expense); }}><Pencil size={16} /></Button>}
                {isPending && <Button variant="ghost" ariaLabel={`Cancelar ${expense.description}`} title="Cancelar" onClick={() => setPendingCancel(expense)}>Cancelar</Button>}
                {isRecurring && <Button variant="ghost" ariaLabel={`Ver série de ${expense.description}`} title="Ver série" onClick={() => viewSeries(expense)}><Repeat size={16} /></Button>}
                {isRecurring && expense.recurrenceGroupId && <Button variant="ghost" ariaLabel={`Editar série de ${expense.description}`} title="Editar série" onClick={() => { setActionError(null); setSeriesEditExpense(expense); }}>Editar série</Button>}
                {isRecurring && expense.recurrenceGroupId && <Button variant="ghost" ariaLabel={`Cancelar série de ${expense.description}`} title="Cancelar série" onClick={() => setPendingSeriesCancel(expense)}>Cancelar série</Button>}
                <Button variant="ghost" ariaLabel={`Excluir ${expense.description}`} title="Excluir" onClick={() => setPendingDelete(expense)}><Trash2 size={16} /></Button>
              </div>
            )}
          </div>
        );
      })}</div>}
      {totalCount > EXPENSE_PAGE_SIZE && <div style={{ display: "flex", justifyContent: "space-between", marginTop: 14, alignItems: "center" }}>
        <span style={{ color: C.light, fontSize: 12 }}>Página {page} de {totalPages}</span>
        <div style={{ display: "flex", gap: 8 }}>
          <Button variant="outline" disabled={page === 1} onClick={() => setPage(value => value - 1)}>Anterior</Button>
          <Button variant="outline" disabled={page >= totalPages} onClick={() => setPage(value => value + 1)}>Próxima</Button>
        </div>
      </div>}
    </Card>

    {formExpense !== undefined && <ExpenseForm initialExpense={formExpense} submitting={submitting} submitError={actionError} onSubmit={submitExpense} onCancel={() => !submitting && setFormExpense(undefined)} />}
    {seriesEditExpense && <ExpenseSeriesForm expense={seriesEditExpense} submitting={submitting} submitError={actionError} onSubmit={submitSeries} onCancel={() => !submitting && setSeriesEditExpense(null)} />}

    {payingExpense && (
      <div role="dialog" aria-modal="true" aria-labelledby="expense-pay-title" style={{ position: "fixed", inset: 0, background: C.overlay, display: "flex", alignItems: "center", justifyContent: "center", zIndex: 50, padding: 16 }}>
        <Card style={{ width: "100%", maxWidth: 420 }}>
          <Label style={{ color: C.ink, fontSize: 13, letterSpacing: 0.5 }}>Pagar despesa</Label>
          <p style={{ fontFamily: font, fontSize: 13.5, color: C.gray, lineHeight: 1.55, margin: "12px 0 16px" }}>
            Confirme o pagamento de <b style={{ color: C.ink }}>{payingExpense.description}</b> ({money(payingExpense.amount)}).
          </p>
          <label htmlFor="expense-payment-method" style={labelStyle()}>Forma de pagamento</label>
          <Select id="expense-payment-method" ariaLabel="Forma de pagamento" value={paymentMethod} options={PAYMENT_OPTIONS} disabled={processing} onChange={setPaymentMethod} style={{ width: "100%" }} />
          {actionError && <div role="alert" style={errorTextStyle()}>{actionError}</div>}
          <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, marginTop: 18 }}>
            <Button variant="outline" onClick={() => setPayingExpense(null)} disabled={processing}>Cancelar</Button>
            <Button onClick={confirmPay} disabled={processing}>{processing ? "Pagando..." : "Confirmar pagamento"}</Button>
          </div>
        </Card>
      </div>
    )}

    {pendingCancel && <ConfirmDialog title="Cancelar despesa" confirmLabel="Cancelar despesa" busy={processing} onConfirm={confirmCancel} onCancel={() => !processing && setPendingCancel(null)}>Deseja cancelar {pendingCancel.description}?</ConfirmDialog>}
    {pendingDelete && <ConfirmDialog title="Excluir despesa" confirmLabel="Excluir despesa" busy={processing} onConfirm={confirmDelete} onCancel={() => !processing && setPendingDelete(null)}>Deseja excluir {pendingDelete.description}? Esta ação não pode ser desfeita.</ConfirmDialog>}
    {pendingSeriesCancel && <ConfirmDialog title="Cancelar série" confirmLabel="Cancelar série" busy={processing} onConfirm={confirmSeriesCancel} onCancel={() => !processing && setPendingSeriesCancel(null)}>Deseja cancelar a série recorrente de {pendingSeriesCancel.description}? As despesas futuras da série deixarão de ser criadas.</ConfirmDialog>}
  </div>;
}